import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'

import { useAdminQuery } from '../api'
import { useAdminContext } from '../session'
import type { SpendersReport } from '../types'

/**
 * Qui a avancé combien.
 *
 * La question se pose une fois, à la fin, et elle fâche si personne ne peut y
 * répondre avec des chiffres. Chacun sous son prénom, ses lignes, son total —
 * et la part que cela représente sur le budget, sans quoi un total ne dit pas
 * s'il est gros ou petit.
 *
 * Le regroupement se fait sur l'auteur de la saisie : on enregistre ce qu'on
 * vient de payer. Un champ « payé par » séparé resterait vide neuf fois sur
 * dix, et un tableau à moitié rempli ne règle aucune discussion.
 *
 * Les lignes se déplient : on lit d'abord les totaux, on ouvre celui qui
 * surprend. Tout afficher d'emblée rendrait la comparaison illisible dès la
 * troisième personne.
 */
export function SpendersPanel({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const { event } = useAdminContext()
  const [open, setOpen] = useState<string | null>(null)

  const query = useAdminQuery<SpendersReport>(
    ['admin-spenders', eventId],
    `/admin/events/${eventId}/finance/spenders`,
    eventId,
  )

  if (query.isPending) return <Skeleton className="h-40 w-full" />
  if (query.isError) {
    return <p className="text-sm text-state-danger">{query.error.message}</p>
  }

  const report = query.data
  if (!report) return null

  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: report.currency }, i18n.language)

  const share = (minor: number) =>
    report.budget_minor > 0 ? Math.round((minor / report.budget_minor) * 100) : null

  return (
    <Card className="mt-8">
      {/* Sur un telephone, le titre et le total se tassaient l'un contre
          l'autre en deux colonnes de rien. Ils s'empilent, et le total ne
          repasse a droite que quand la largeur le permet. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.finance.spenders')}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-content-secondary">
            {t('admin.finance.spendersHint')}
          </p>
        </div>
        <p className="shrink-0 sm:text-right">
          <span className="block text-caption uppercase tracking-wide text-content-muted">
            {t('admin.finance.spendersTotal')}
          </span>
          <span className="font-display text-heading-md tabular-nums text-content-primary">
            {money(report.total_minor)}
          </span>
        </p>
      </div>

      {report.spenders.length === 0 ? (
        <p className="mt-5 text-sm text-content-secondary">
          {t('admin.finance.spendersEmpty')}
        </p>
      ) : (
        <ul className="mt-5 grid gap-2">
          {report.spenders.map((spender) => {
            const key = spender.user_id ?? spender.name
            const expanded = open === key
            const percent = share(spender.total_minor)
            return (
              <li key={key} className="rounded-lg border border-line-subtle">
                {/* Le nom et le montant restent sur la meme ligne : c'est la
                    comparaison qu'on vient chercher. Le decompte et la pastille
                    « sans justificatif » passent dessous, ou ils faisaient
                    deborder la rangee sur un ecran de telephone. */}
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : key)}
                  aria-expanded={expanded}
                  className="w-full px-4 py-3 text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                      {spender.name}
                    </span>
                    <span className="shrink-0 font-display text-heading-sm tabular-nums text-content-primary">
                      {money(spender.total_minor)}
                    </span>
                    <span aria-hidden className="shrink-0 text-content-muted">
                      {expanded ? '−' : '+'}
                    </span>
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-caption text-content-muted">
                      {t('admin.finance.spendersCount', { count: spender.expense_count })}
                      {percent !== null ? ` · ${t('admin.finance.spendersShare', { percent })}` : ''}
                    </span>
                    {spender.missing_receipts > 0 ? (
                      <Badge tone="ember">
                        {t('admin.finance.missingReceipts', {
                          count: spender.missing_receipts,
                        })}
                      </Badge>
                    ) : null}
                  </span>
                </button>

                {expanded ? (
                  <ul className="border-t border-line-subtle px-4 py-2">
                    {spender.entries.map((entry) => (
                      <li key={entry.expense_id} className="py-1.5">
                        <span className="flex items-baseline gap-3">
                          <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                            {entry.label}
                          </span>
                          <span className="shrink-0 text-sm tabular-nums text-content-primary">
                            {money(entry.amount_minor)}
                          </span>
                        </span>
                        <span className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-caption tabular-nums text-content-muted">
                            {formatDate(entry.spent_on, i18n.language, event?.timezone ?? 'UTC')}
                          </span>
                          {!entry.has_receipt ? (
                            <span
                              className={cn('text-caption text-ember-300')}
                              title={t('admin.finance.noReceiptHint')}
                            >
                              {t('admin.finance.noReceipt')}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
